import React, { useEffect, useRef, useState, memo } from 'react';

import { Autosuggest, AutosuggestProps, FormField, Input, InputProps, Spinner } from '@amzn/awsui-components-react-v3';
import { NonCancelableCustomEvent } from '@amzn/awsui-components-react-v3/polaris/internal/events';

import { useMetrics, logger, useArgoContext } from 'appSelectorContext';
import clsx from 'clsx';
import { isEmpty, trimEnd, trimStart } from 'lodash';

import { METRIC_PUBLISHER_NAMES } from '../../constants';
import barcodeImg from '../../images/barcode.jpeg';

import './scanInput.scss';

interface ScanInputProps {
  onScan?: (value: string) => void;
  onChange?: (value: string) => any;
  onKeyDown?: (value: any) => void;
  inputValue?: string;
  additionalChars?: string; // allow more characters than the default regex
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  ariaLabel?: string;
  label?: string;
  description?: string;
  isValid?: boolean;
  autoFocus?: boolean;
  clearOnSubmit?: boolean;
  children?: React.ReactNode;
  errors?: string | React.ReactNode;
  validationErrorText?: string;
  loading?: boolean;
  disableCopy?: boolean;
  controlId?: string;
  disableFocus?: boolean;
  validateRegex?: string;
  autoSuggest?: boolean;
  options?: any[];
  autoSuggestProps?: Pick<
    AutosuggestProps,
    'options' | 'loadingText' | 'statusType' | 'virtualScroll' | 'expandToViewport' | 'empty'
  >;
}

export const ScanInput = memo(
  ({
    onScan,
    inputValue = '',
    placeholder,
    disabled = false,
    ariaLabel,
    label = '',
    additionalChars = '',
    description = '',
    isValid,
    onChange,
    onKeyDown,
    autoFocus = true,
    clearOnSubmit = false,
    children = null,
    readOnly = false,
    errors = undefined,
    loading,
    disableCopy = false,
    controlId = 'scan-input',
    disableFocus = false,
    validateRegex,
    validationErrorText = '',
    autoSuggest = false,
    autoSuggestProps = {},
  }: ScanInputProps) => {
    const [value, setValue] = useState<string>(inputValue);
    const [errorText, setErrorText] = useState<string | React.ReactNode>('');
    const [tabbed, setTabbed] = useState<boolean>(true);
    const inputRef = useRef();
    const metrics = useMetrics();
    const context = useArgoContext();
    const publisher = metrics.initialMetricsPublisher.newChildActionPublisherForMethod(
      METRIC_PUBLISHER_NAMES.SCANNER_INPUT_TYPE,
    );

    const onHandleBlur = () => {
      // auto focus
      !disableFocus && tabbed && inputRef?.current?.focus();
      setTabbed(false);
    };

    useEffect(() => {
      setValue(inputValue);
    }, [inputValue]);

    useEffect(() => {
      if (!loading && clearOnSubmit) {
        setValue('');
      }
    }, [loading, clearOnSubmit]);

    useEffect(() => {
      setErrorText(errors);
    }, [errors]);

    useEffect(() => {
      const inputElem = document.getElementById(controlId);
      if (inputElem && disableCopy) {
        inputElem.addEventListener('paste', (e) => e.preventDefault());
      }
    }, [disableCopy, controlId]);

    useEffect(() => {
      if (autoFocus && !!inputRef?.current) {
        setTimeout(() => {
          inputRef?.current?.focus();
        }, 100);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputRef?.current]);

    const onInputKeyDown = ({ detail, ...rest }: { detail: InputProps.KeyDetail }) => {
      onKeyDown && onKeyDown(detail);
      if ((detail.key === 'Enter' || detail.key === 'Tab') && value?.length > 0) {
        detail.key === 'Tab' && setTabbed(true);
        logger.info(
          `input keydown event triggered: ${JSON.stringify({
            value,
            withUser: context.user,
          })} `,
        );
        publisher.publishCounterMonitor(detail.key, 1);

        let regexExp = validateRegex;

        if (typeof regexExp === 'undefined') {
          regexExp = '^[a-zA-Z0-9 _.,-]*$';
        }

        if (additionalChars) {
          regexExp = `^[a-zA-Z0-9 _.,${additionalChars}-]*$`;
        }

        if (value.trim().length === 0) {
          return;
        }

        const trimmedValue = trimEnd(trimStart(value));

        // const string = '^[a-zA-Z0-9_. -]*$';
        const regexp = new RegExp(regexExp);

        if (!regexp.test(trimmedValue?.toUpperCase())) {
          let prefix = label;
          if (!prefix) {
            prefix = placeholder || 'value';
          }
          setErrorText(
            !isEmpty(validationErrorText)
              ? validationErrorText
              : `${prefix} should not contain space or certain special characters`,
          );
          return;
        }

        if (trimmedValue.length > 128) {
          setErrorText(`Max 128 characters are allowed`);
          return;
        }

        onScan && onScan(trimmedValue);
        loading === undefined && clearOnSubmit && setValue('');
      }
    };

    const onHandleInputChange = (event: NonCancelableCustomEvent<InputProps.ChangeDetail>) => {
      const value = event.detail.value;
      setValue(value);
      setErrorText('');
      onChange && onChange(trimEnd(trimStart(value)));
    };

    const currentProps = {
      autoFocus,
      value,
      onChange: onHandleInputChange,
      onKeyDown: onInputKeyDown,
      placeholder: placeholder || 'Scan',
      disabled,
      ariaLabel: ariaLabel || 'scan-input',
      readOnly: loading || readOnly,
      controlId,
      ref: inputRef,
      autoComplete: false,
      onBlur: onHandleBlur,
    };
    let selectedProps: any = autoSuggest ? { ...currentProps, ...autoSuggestProps, autoFocus } : currentProps;

    return (
      <div className={clsx('scan-input', { disabled, errorText })}>
        <FormField errorText={errorText} label={label} description={description} stretch>
          {autoSuggest ? <Autosuggest autoFocus {...selectedProps} /> : <Input {...selectedProps} />}
          {loading ? (
            <div className="loading-icon">
              <Spinner size="normal" variant="disabled" />
            </div>
          ) : (
            <img alt="scan input" src={barcodeImg} width={40} height={27} />
          )}
        </FormField>
        {children}
      </div>
    );
  },
);
